export interface Form {
    uuId: string;
    id: number;
    pageId: number;
    sections: FormSections[];
    forwardUrl: string;
    error: boolean;
    errorMessage: string;
    metadata: FormMetadata;
}

export interface Organization {
    userId: number;
    companyName: string;
    hidden: boolean;
    elements: FormElements[];
}

export interface FormSections {
    id: number;
    type: number;
    displayOrder: number;
    name: string;
    border: boolean;
    elements: FormElements[];
    applicants: Applicant[];
    organization: Organization;
    eventName: string;
    eventDate: number;
    required: boolean;
    canRegisterSelf: boolean;
    canRegisterLinkedProfiles: boolean;
    canRegisterGuest: boolean;
    availableLinkedProfiles: LinkedProfile[];
    selectedRegistrants: SelectedRegistrant[];
    showReservationInfo: boolean;
    key: number;
    totalAmountDue: number;
    organizationName: string;
    errorMessage: string;
    totalAmount: number;
    totalDiscountedAmount: number;
    showPrices: boolean;
}

export interface Applicant {
    id: number;
    self: boolean;
    linked: boolean;
    showLink: boolean;
    showUnlink: boolean;
    showDelete: boolean;
    userId: number;
    firstName: string;
    lastName: string;
    hidden: boolean;
    elements: FormElements[];
}

export interface SelectedRegistrant {
    applicantId: number;
    elements: FormElements[];
    firstName: string;
    guest: boolean;
    hidden: boolean;
    id: number;
    lastName: string;
    memberTypeId: number;
    reserved: boolean;
    self: boolean;
    userId: number;
}

export interface LinkedProfile {
    userId: number;
    applicantId: number;
    firstName: string;
    lastName: string;
}

export interface FormMetadata {
    firstPage: boolean;
    lastPage: boolean;
    submitPage: boolean;
    clock: number;
    clockDelay: number;
}

export interface FormElements {
    type: number;
    key: number;
    templateOptions: FormTemplateOptions;
    value?: any;
}

export interface FormTemplateOptions {
    sectionId: number;
    organizationField: boolean;
    disableWhiteout: boolean;
    label: string;
    value: string;
    required: boolean;
    readonly: boolean;
    disabled: boolean;
    minlength: number;
    maxlength: number;
    displayType: number;
    currency: boolean;
    currencyId: number;
    decimal: boolean;
    digitsAfterDecimal: number;
    multiplier: number;
    priceField: number;
    adminOverrideEligible: boolean;
    selections: Selection[];
    attributeTypeLabels: AttributeTypeLabel[];
    values: AddressValues[] | EmailPhoneValues[];
    prefixEnabled: boolean;
    middleNameEnabled: boolean;
    middleInitialEnabled: boolean;
    suffixEnabled: boolean;
    firstName: string;
    middleName: string;
    middleInitial: string;
    lastName: string;
    prefixes: PrefixSuffix[];
    suffixes: PrefixSuffix[];
    display: boolean;
    company: string;
    companyName: string;
    creditCards: Selection[];
    addressLine1: string;
    addressLine2: string;
    addressCity: string;
    addressZip: string;
    states: Selection[];
    countries: Selection[];
    name: string;
    email: string;
    phone: string;
    displayName: string;
    isApplicant: boolean;
    memberSelection?: boolean;
    multiple?: boolean;
    pricingSchema?: number;
    attributeTypeId?: number;
    priceMultiplier?: number;
}

export interface AttributeTypeLabel {
    id: number;
    displayOrder: number;
    name: string;
    status: number;
}

export interface TypeLabel {
    id: number;
    name: string;
    value?: string;
    description?: string;
}

export interface AddressValues {
    city: string;
    countries: AddressInformation[];
    line1: string;
    line2: string;
    states: AddressInformation[];
    typeLabel: TypeLabel;
    zip: string;
}

export interface EmailPhoneValues {
    email?: string;
    phone?: string;
    typeLabel: TypeLabel;
}

export interface AccountLimits {
    serviceId: number;
    accountLimitsExceeded: boolean;
    restrictAccess: boolean;
    exceededLimitsDate: number;
    underLimitsDate: number;
    exempt: boolean;
    daysUntilRestricted: number;
}

export interface AddressInformation {
    description: string;
    id: number;
    name: string;
    selected: boolean;
    value: string;
}

export interface Selection {
    id: number;
    name: string;
    value: string;
    disabled: boolean;
    readonly: boolean;
    description: string;
    selected: boolean;
    price: number;
    displayDiscountCodePrice: boolean;
    displayAdminOverridePrice: boolean;
    discountCodePrice: number;
    adminOverridePrice: number;
}

export interface SaveRequest {
    fieldValues?: FieldValue[];
    organization?: OrganizationFieldValue;
    applicants?: ApplicantFieldValue[];
    registrants?: RegistrantFieldValue[];
}

export interface OrganizationFieldValue {
    fieldValues?: FieldValue[];
}

export interface RegistrantFieldValue {
    id: number;
    sectionId: number;
    fieldValues: FieldValue[];
}

export interface ApplicantFieldValue {
    id: number;
    sectionId: number;
    fieldValues: FieldValue[];
    self: boolean;
}

export interface FieldValue {
    fieldId: number;
    city?: string;
    line1?: string;
    line2?: string;
    zip?: string;
    country?: any;
    state?: any;
    email?: string;
    phone?: string;
    typeLabel?: TypeLabel;
    valueIds?: ValueId[];
    value?: string;
}

export interface ValueId {
    id: number;
    name: string;
}

export interface SaveResponse {
    response: any;
    errors: any[];
    refreshPage: boolean;
}

export interface SubmitRequest {
    sendInvoice: boolean;
    uuId: string;
}

export interface SubmitResponse {
    submissionId: number;
}

export interface SubmitCreditCardPaymentRequest {
    sendInvoice: boolean;
    uuId: string;
    firstName: string;
    lastName: string;
    company: string;
    expirationMonth: number;
    expirationYear: number;
    state: string;
    country: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    zip: string;
    reCaptchaResponseToken: string;
    cardNumber: string;
    securityCode: string;
}

export interface SubmitEFTPaymentRequest {
    sendInvoice: boolean;
    uuId: string;
    name: string;
    accountNumber: string;
    routingNumber: string;
    accountType: string;
    accountPurpose: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    email: string;
    phone: string;
    reCaptchaResponseToken: string;
}

export interface SubmitCreditCardAndEFTPaymentRequest {
    sendInvoice: boolean;
    uuId: string;
    firstName: string;
    lastName: string;
    company: string;
    expirationMonth: number;
    expirationYear: number;
    state: string;
    country: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    zip: string;
    reCaptchaResponseToken: string;
    cardNumber: string;
    securityCode: string;
    email: string;
    phone: string;
    name: string;
    accountNumber: string;
    routingNumber: string;
    accountType: string;
    accountPurpose: string;
}

export interface SubmitOfflinePaymentRequest {
    sendInvoice: boolean;
    uuId: string;
    referenceNumber: string;
    email: string;
    paymentDate: string;
    offlinePaymentTypeId: number;
}

export interface SubmitInvoicePaymentRequest {
    sendInvoice: boolean;
    uuId: string;
    email: string;
}

export interface SubmitPaymentResponse {
    submissionId: number;
}

export interface FinishRequest {
    submissionId: number;
    forwardUrl: string;
}

export interface SelfApplicationExistsResponse {
    selfApplicantExists: boolean;
}

export interface SelfApplicantResponse {
    selfApplicantExists: boolean;
}

export interface MembershipStatusResponse {
    membershipStatus: number;
}

export interface PrefixSuffix {
    id: number;
    name: string;
    value: string;
    description: string;
    selected: boolean;
}


export interface NameFieldFormValues {
    firstName: string;
    lastName: string;
    middleName: string;
    middleInitial: string;
    prefix: string;
    suffix: string;
}

export interface FileUploadMetadata {
    name: string;
    size: number;
    type: string;
    url: string;
    delete_url: string;
    delete_type: string;
}

export interface FileEntry {
    value?: string;
    fileId?: string;
    /**
     * Form uuId
     */
    uuId: string;
}

export interface FileUploadResponse {
    files: FileUploadMetadata[];
    fileId: string;
}

export interface FileUploadAwsResponse {
    url: string;
    key: string;
}

export interface PaymentState {
    hidePaymentBtns: boolean;
    hideSubmit: boolean;
    hideInvoiceBtn: boolean;
    hasOfflinePayment: boolean;
    hasEFT: boolean;
    hasCC: boolean;
    hasInvoicing: boolean;
    paymentPage: boolean;
    hasMultiplePayments: boolean;
    paymentType: string;
}

export interface MonthOption {
    value: number;
    label: string;
}

export interface YearOption {
    value: number;
    label: string;
}

export type ModalType = "HIDDEN" | "RESTART" | "ALERT"

export interface ModalContent {
    type: ModalType;
    preventClose?: boolean;
    data?: any;
    hideCallback?: Function
}

export enum SectionType {
    APPLICANT = "APPLICANT",
    REGISTRANT = "REGISTRANT",
    //ORGANIZATION = "ORGANIZATION", For now keeps commented until bug fix on the backend
    REGULAR = "REGULAR"
}

export interface SummarySection {
    type: number;
    displayOrder: number;
    name: string;
    border: boolean;
    elements: SummaryElements[];
    showPrices: boolean;
    totalAmount: number;
    totalDiscountedAmount: number;
    key: number;
}

export interface SummaryElements {
    type: number;
    key: number;
    templateOptions: SummaryTemplateOptions;
}

export interface SummaryTemplateOptions {
    sectionId: number;
    discountCode: string;
    validDiscountCode: boolean;
    sections: SummaryFormSections[];
}

export interface SummaryFormSections {
    id: number;
    type: number;
    displayOrder: number;
    name: string;
    border: boolean;
    elements: SummarySectionElements[];
    applicants: SummarySectionApplicant[];
    organization?: SummarySectionOrganization;
    eventName: string;
    eventDate: number;
    required: boolean;
    canRegisterSelf: boolean;
    canRegisterLinkedProfiles: boolean;
    canRegisterGuest: boolean;
    availableLinkedProfiles: LinkedProfile[];
    selectedRegistrants: SummarySectionSelectedRegistrant[];
    showReservationInfo: boolean;
    key: number;
    totalAmountDue: number;
    organizationName: string;
    errorMessage: string;
    totalAmount: number;
    totalDiscountedAmount: number;
    showPrices: boolean;
    adminOverride: boolean;
    callback: Function;
}

export interface SummarySectionElements {
    id: number;
    type: number;
    key: number;
    sectionId: number;
    organizationField: boolean;
    disableWhiteout: boolean;
    label: string;
    value: string;
    required: boolean;
    readonly: boolean;
    minlength: number;
    maxlength: number;
    displayType: number;
    currency: boolean;
    currencyId: number;
    decimal: boolean;
    digitsAfterDecimal: number;
    multiplier: number;
    priceField: number;
    adminOverrideEligible: boolean;
    selections: Selection[];
    attributeTypeLabels: AttributeTypeLabel[];
    values: AddressValues[] | EmailPhoneValues[];
    prefixEnabled: boolean;
    middleNameEnabled: boolean;
    middleInitialEnabled: boolean;
    suffixEnabled: boolean;
    firstName: string;
    middleName: string;
    middleInitial: string;
    lastName: string;
    prefixes: PrefixSuffix[];
    suffixes: PrefixSuffix[];
    display: boolean;
    company: string;
    companyName: string;
    creditCards: Selection[];
    addressLine1: string;
    addressLine2: string;
    addressCity: string;
    addressZip: string;
    states: Selection[];
    countries: Selection[];
    name: string;
    email: string;
    phone: string;
    adminOverride: boolean;
    callback: Function;
    registrantId: number;
    selectionId: number;
    applicantId: number;
    sectionType: SectionType;
    displayName: string;
    priceMultiplier: number;
    pricingSchema?: number;
    attributeTypeId?: number;
    templateOptions?: { pricingSchema?: number; attributeTypeId?: number };
}

export interface SummarySectionApplicant {
    id: number;
    self: boolean;
    linked: boolean;
    showLink: boolean;
    showUnlink: boolean;
    showDelete: boolean;
    userId: number;
    firstName: string;
    lastName: string;
    hidden: boolean;
    elements: SummarySectionElements[];
}

export interface SummarySectionOrganization {
    userId: number;
    companyName: string;
    hidden: boolean;
    elements: SummarySectionElements[];
}

export interface SummarySectionSelectedRegistrant {
    applicantId: number;
    elements: SummarySectionElements[];
    firstName: string;
    guest: boolean;
    hidden: boolean;
    id: number;
    lastName: string;
    memberTypeId: number;
    reserved: boolean;
    self: boolean;
    userId: number;
}

export interface AdminPriceOverrideRequest {
    adminPricing: AdminPricing
}

export interface AdminPricing {
    adminPricedApplicantFields: AdminPricedApplicantField[];
    adminPricedRegistrantFields: AdminPricedRegistrantField[];
    adminPricedOrganizationFields: AdminPricedOrganizationField[];
    adminPricedFields: AdminPricedField[];
}

export interface AdminPricedApplicantField {
    fieldId: number;
    price: number;
    selectionId: number;
    applicantId: number;
}

export interface AdminPricedRegistrantField {
    fieldId: number;
    price: number;
    selectionId: number;
    registrantId: number;
}

export interface AdminPricedOrganizationField {
    fieldId: number;
    price: number;
    selectionId: number;
    organizationId: number;
}

export interface AdminPricedField {
    fieldId: number;
    price: number;
    selectionId: number;
}

export enum WrapperType {
    REGISTRANT = 'registrant',
    APPLICANT = 'applicant'
}
